import React, { useState, useEffect } from 'react'
import { Badge } from '../ui/Badge'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select'
import { 
  Ambulance, 
  Heart, 
  Stethoscope, 
  Shield, 
  Clock, 
  Users, 
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Info,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import apiService from '../../services/apiService'

const AmbulanceTypes = ({ selectedType, onTypeSelect }) => {
  const [ambulanceTypes, setAmbulanceTypes] = useState([])
  const [filteredTypes, setFilteredTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(2) // Show 2 ambulance types per page
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('')
  const [availabilityFilter, setAvailabilityFilter] = useState('all')
  const [sortBy, setSortBy] = useState('name')

  const fetchAmbulanceTypes = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Use the correct API service
      const response = await apiService.getAmbulanceTypes()
      
      // Handle different response structures
      let types = []
      if (response.success && response.data) {
        types = response.data
      } else if (Array.isArray(response)) {
        types = response
      }
      
      // Transform API data to match component structure
      const transformedTypes = types.map(type => ({
        id: type.type_id,
        name: type.name,
        description: `${type.name} ambulance service with specialized medical equipment`,
        price: `₹${type.base_fare}/km`,
        icon: getIconForType(type.name),
        features: getFeaturesForType(type.name),
        responseTime: getResponseTimeForType(type.name),
        capacity: getCapacityForType(type.name),
        availability: getAvailabilityForType(type.name),
        suitableFor: getSuitableForType(type.name),
        per_km_rate: type.per_km_rate
      }))
      
      setAmbulanceTypes(transformedTypes)
      
    } catch (err) {
      setError('Unable to load ambulance types. Please try again.')
      
      // Fallback to default types
      const fallbackTypes = [
        {
          id: 'fallback-1',
          name: 'Basic Life Support (BLS)',
          description: 'Standard ambulance with basic medical equipment for non-critical patients',
          price: '₹200/km',
          icon: 'ambulance',
          features: ['Basic medical equipment', 'Trained EMT', 'Oxygen support', 'First aid supplies', 'Stretcher transport'],
          responseTime: '15-20 mins',
          capacity: '1 patient + 2 attendants',
          availability: 'High',
          suitableFor: ['Non-emergency transfers', 'Routine medical transport', 'Discharge from hospital']
        },
        {
          id: 'fallback-2',
          name: 'Advanced Life Support (ALS)',
          description: 'Advanced medical equipment with paramedic for critical patients',
          price: '₹350/km',
          icon: 'heart',
          features: ['Advanced medical equipment', 'Paramedic on board', 'Cardiac monitoring', 'IV therapy capability', 'Advanced airway management', 'Defibrillator'],
          responseTime: '10-15 mins',
          capacity: '1 patient + 1 attendant',
          availability: 'Medium',
          suitableFor: ['Critical emergencies', 'ICU transfers', 'Cardiac emergencies']
        },
        {
          id: 'fallback-3',
          name: 'Critical Care',
          description: 'ICU-level care with specialized equipment and critical care team',
          price: '₹500/km',
          icon: 'shield',
          features: ['ICU-level equipment', 'Critical care team', 'Ventilator support', 'Advanced monitoring', 'Specialized medications', 'Emergency procedures'],
          responseTime: '5-10 mins',
          capacity: '1 patient only',
          availability: 'Limited',
          suitableFor: ['Critical emergencies', 'ICU transfers', 'Life-threatening conditions']
        }
      ]
      setAmbulanceTypes(fallbackTypes)
    } finally {
      setLoading(false)
    }
  }

  // Helper functions to generate appropriate data for each ambulance type
  const getIconForType = (name) => {
    if (name.toLowerCase().includes('basic')) return 'ambulance'
    if (name.toLowerCase().includes('advanced')) return 'heart'
    if (name.toLowerCase().includes('critical')) return 'shield'
    if (name.toLowerCase().includes('neonatal')) return 'stethoscope'
    if (name.toLowerCase().includes('pediatric')) return 'stethoscope'
    if (name.toLowerCase().includes('psychiatric')) return 'shield'
    if (name.toLowerCase().includes('bariatric')) return 'ambulance'
    return 'ambulance'
  }

  const getFeaturesForType = (name) => {
    const baseFeatures = ['Trained medical staff', 'Basic medical equipment']
    
    if (name.toLowerCase().includes('basic')) {
      return [...baseFeatures, 'Oxygen support', 'First aid supplies', 'Stretcher transport']
    }
    if (name.toLowerCase().includes('advanced')) {
      return [...baseFeatures, 'Paramedic on board', 'Cardiac monitoring', 'IV therapy capability', 'Defibrillator']
    }
    if (name.toLowerCase().includes('critical')) {
      return [...baseFeatures, 'ICU-level equipment', 'Critical care team', 'Ventilator support', 'Advanced monitoring']
    }
    if (name.toLowerCase().includes('neonatal')) {
      return [...baseFeatures, 'Neonatal incubator', 'Specialized neonatal care', 'Temperature control', 'Monitoring equipment']
    }
    if (name.toLowerCase().includes('pediatric')) {
      return [...baseFeatures, 'Pediatric equipment', 'Child-friendly environment', 'Specialized medications', 'Family support']
    }
    if (name.toLowerCase().includes('psychiatric')) {
      return [...baseFeatures, 'Secure transport', 'Mental health professionals', 'Safety equipment', 'Restraint systems']
    }
    if (name.toLowerCase().includes('bariatric')) {
      return [...baseFeatures, 'Heavy-duty equipment', 'Wide stretcher', 'Specialized lifting', 'Reinforced vehicle']
    }
    
    return baseFeatures
  }

  const getResponseTimeForType = (name) => {
    if (name.toLowerCase().includes('critical')) return '5-10 mins'
    if (name.toLowerCase().includes('advanced')) return '10-15 mins'
    if (name.toLowerCase().includes('neonatal')) return '8-12 mins'
    if (name.toLowerCase().includes('pediatric')) return '10-15 mins'
    if (name.toLowerCase().includes('psychiatric')) return '12-18 mins'
    if (name.toLowerCase().includes('bariatric')) return '15-20 mins'
    return '15-20 mins'
  }

  const getCapacityForType = (name) => {
    if (name.toLowerCase().includes('critical')) return '1 patient only'
    if (name.toLowerCase().includes('neonatal')) return '1 patient + 1 parent'
    if (name.toLowerCase().includes('pediatric')) return '1 patient + 1 parent'
    if (name.toLowerCase().includes('psychiatric')) return '1 patient + 1 attendant'
    if (name.toLowerCase().includes('bariatric')) return '1 patient + 1 attendant'
    return '1 patient + 2 attendants'
  }

  const getAvailabilityForType = (name) => {
    if (name.toLowerCase().includes('critical')) return 'Limited'
    if (name.toLowerCase().includes('neonatal')) return 'Medium'
    if (name.toLowerCase().includes('pediatric')) return 'Medium'
    if (name.toLowerCase().includes('psychiatric')) return 'Medium'
    if (name.toLowerCase().includes('bariatric')) return 'Low'
    if (name.toLowerCase().includes('advanced')) return 'Medium'
    return 'High'
  }

  const getSuitableForType = (name) => {
    if (name.toLowerCase().includes('basic')) {
      return ['Non-emergency transfers', 'Routine medical transport', 'Discharge from hospital']
    }
    if (name.toLowerCase().includes('advanced')) {
      return ['Critical emergencies', 'ICU transfers', 'Cardiac emergencies']
    }
    if (name.toLowerCase().includes('critical')) {
      return ['Critical emergencies', 'ICU transfers', 'Life-threatening conditions']
    }
    if (name.toLowerCase().includes('neonatal')) {
      return ['Newborn transport', 'NICU transfers', 'Premature baby care']
    }
    if (name.toLowerCase().includes('pediatric')) {
      return ['Child emergencies', 'Pediatric transfers', 'Family transport']
    }
    if (name.toLowerCase().includes('psychiatric')) {
      return ['Mental health emergencies', 'Psychiatric transfers', 'Behavioral crises']
    }
    if (name.toLowerCase().includes('bariatric')) {
      return ['Obesity-related transport', 'Heavy patient transfers', 'Specialized care']
    }
    
    return ['General medical transport', 'Emergency services']
  }

  // Filter and sort ambulance types
  useEffect(() => {
    let filtered = [...ambulanceTypes]
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(type => 
        type.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        type.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        type.suitableFor.some(suitable => 
          suitable.toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    }
    
    // Apply availability filter
    if (availabilityFilter !== 'all') {
      filtered = filtered.filter(type => 
        type.availability.toLowerCase() === availabilityFilter.toLowerCase()
      )
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name)
        case 'price':
          return parseFloat(a.price.replace(/[₹,]/g, '')) - parseFloat(b.price.replace(/[₹,]/g, ''))
        case 'response':
          return a.responseTime.localeCompare(b.responseTime)
        case 'availability':
          const availabilityOrder = { 'high': 3, 'medium': 2, 'limited': 1, 'low': 0 }
          return availabilityOrder[b.availability.toLowerCase()] - availabilityOrder[a.availability.toLowerCase()]
        default:
          return 0
      }
    })
    
    setFilteredTypes(filtered)
    setCurrentPage(1) // Reset to first page when filters change
  }, [ambulanceTypes, searchTerm, availabilityFilter, sortBy])

  useEffect(() => {
    fetchAmbulanceTypes()
  }, [])

  const getIcon = (iconName) => {
    switch (iconName) {
      case 'ambulance':
        return <Ambulance className="w-6 h-6" />
      case 'heart':
        return <Heart className="w-6 h-6" />
      case 'shield':
        return <Shield className="w-6 h-6" />
      case 'stethoscope':
        return <Stethoscope className="w-6 h-6" />
      default:
        return <Ambulance className="w-6 h-6" />
    }
  }

  const getAvailabilityColor = (availability) => {
    switch (availability.toLowerCase()) {
      case 'high':
        return 'bg-green-100 text-green-800'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800'
      case 'limited':
        return 'bg-red-100 text-red-800'
      case 'low':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // Pagination calculations
  const totalPages = Math.ceil(filteredTypes.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentTypes = filteredTypes.slice(startIndex, endIndex)

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading ambulance types...</span>
      </div>
    )
  }

  return (
    <div className="w-full">
      {/* Main Title and Description */}
      <div className="pt-16 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Available Ambulance Types</h3>
        <p className="text-sm text-gray-600">
          Choose from our comprehensive range of specialized ambulance services
        </p>
      </div>

      {/* Search and Filter Controls */}
      <div className="space-y-4 mb-6">
        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search ambulance types..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Filter Controls */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <Select value={availabilityFilter} onValueChange={setAvailabilityFilter}>
              <SelectTrigger className="text-sm">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by availability" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Availability</SelectItem>
                <SelectItem value="high">High Availability</SelectItem>
                <SelectItem value="medium">Medium Availability</SelectItem>
                <SelectItem value="limited">Limited Availability</SelectItem>
                <SelectItem value="low">Low Availability</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name (A-Z)</SelectItem>
                <SelectItem value="price">Price (Low to High)</SelectItem>
                <SelectItem value="response">Response Time</SelectItem>
                <SelectItem value="availability">Availability</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={fetchAmbulanceTypes}
            variant="outline"
            size="sm"
            className="text-blue-600 hover:text-blue-700 w-full sm:w-auto"
          >
            <RefreshCw className="w-4 h-4 mr-2 sm:mr-0" />
            <span className="sm:hidden">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Ambulance className="w-5 h-5 text-blue-600" />
          <span className="text-sm font-medium text-gray-700">
            Showing {filteredTypes.length} of {ambulanceTypes.length} ambulance types
          </span>
        </div>
        {filteredTypes.length > 0 && (
          <span className="text-sm text-gray-500">
            Page {currentPage} of {totalPages}
          </span>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
          <div className="flex items-center">
            <AlertCircle className="w-4 h-4 text-yellow-600 mr-2" />
            <span className="text-sm text-yellow-800">{error}</span>
          </div>
        </div>
      )}

      {/* No Results Message */}
      {filteredTypes.length === 0 && !loading && (
        <div className="text-center py-8">
          <Ambulance className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No ambulance types found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || availabilityFilter !== 'all' 
              ? 'Try adjusting your search or filter criteria.'
              : 'No ambulance types are currently available.'
            }
          </p>
          {(searchTerm || availabilityFilter !== 'all') && (
            <Button
              onClick={() => {
                setSearchTerm('')
                setAvailabilityFilter('all')
              }}
              variant="outline"
            >
              Clear Filters
            </Button>
          )}
        </div>
      )}

      {/* Ambulance Types Grid */}
      {filteredTypes.length > 0 && (
        <div className="grid grid-cols-1 gap-4 lg:gap-6">
          {currentTypes.map((type) => (
          <div
            key={type.id}
            className={`relative border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
              selectedType === type.id
                ? 'border-blue-500 bg-blue-50 shadow-md'
                : 'border-gray-200 hover:border-gray-300 bg-white'
            }`}
            onClick={() => onTypeSelect(type.id)}
          >
            {/* Header with icon, name, and price */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3 flex-1">
                <div className={`p-2 rounded-lg flex-shrink-0 ${
                  selectedType === type.id ? 'bg-blue-100' : 'bg-gray-100'
                }`}>
                  {getIcon(type.icon)}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 text-base truncate">{type.name}</h3>
                  <p className="text-xs text-gray-600 mt-1 line-clamp-2">{type.description}</p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2 ml-3">
                <div className="text-lg font-bold text-blue-600">{type.price}</div>
                <Badge className={`${getAvailabilityColor(type.availability)} text-xs`}>
                  {type.availability}
                </Badge>
              </div>
            </div>

            {/* Quick info row */}
            <div className="grid grid-cols-2 gap-3 text-xs mb-3">
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3 text-gray-400 flex-shrink-0" />
                <span className="text-gray-600 truncate">{type.responseTime}</span>
              </div>
              <div className="flex items-center gap-1">
                <Users className="w-3 h-3 text-gray-400 flex-shrink-0" />
                <span className="text-gray-600 truncate">{type.capacity}</span>
              </div>
            </div>

            {/* Key Features - Compact */}
            <div className="mb-3">
              <div className="flex flex-wrap gap-1">
                {type.features.slice(0, 2).map((feature, index) => (
                  <Badge key={index} variant="outline" className="text-xs px-2 py-0.5">
                    {feature}
                  </Badge>
                ))}
                {type.features.length > 2 && (
                  <Badge variant="outline" className="text-xs px-2 py-0.5">
                    +{type.features.length - 2} more
                  </Badge>
                )}
              </div>
            </div>

            {/* Suitable for - Compact */}
            <div className="border-t pt-2">
              <div className="text-xs text-gray-600 line-clamp-2">
                <span className="font-medium">Suitable for:</span> {type.suitableFor.slice(0, 2).join(' • ')}
                {type.suitableFor.length > 2 && ' • ...'}
              </div>
            </div>

            {/* Selection indicator */}
            {selectedType === type.id && (
              <div className="absolute top-2 right-2">
                <CheckCircle className="w-5 h-5 text-blue-600" />
              </div>
            )}
          </div>
          ))}
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between mt-6 gap-4">
          <div className="text-sm text-gray-700 text-center sm:text-left">
            Showing {startIndex + 1} to {Math.min(endIndex, filteredTypes.length)} of {filteredTypes.length} results
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="text-xs sm:text-sm"
            >
              <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline ml-1">Previous</span>
            </Button>
            
            {/* Page Numbers - Responsive */}
            <div className="flex items-center gap-1">
              {/* Show all pages on desktop, limited on mobile */}
              {totalPages <= 5 ? (
                // Show all pages if 5 or fewer
                Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <Button
                    key={page}
                    variant={currentPage === page ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePageChange(page)}
                    className="w-7 h-7 sm:w-8 sm:h-8 p-0 text-xs sm:text-sm"
                  >
                    {page}
                  </Button>
                ))
              ) : (
                // Show smart pagination for more than 5 pages
                <>
                  {/* Always show first page */}
                  <Button
                    variant={currentPage === 1 ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePageChange(1)}
                    className="w-7 h-7 sm:w-8 sm:h-8 p-0 text-xs sm:text-sm"
                  >
                    1
                  </Button>
                  
                  {/* Show ellipsis if current page is far from start */}
                  {currentPage > 3 && (
                    <span className="px-1 text-gray-500 text-xs sm:text-sm">...</span>
                  )}
                  
                  {/* Show pages around current page */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => 
                      page > 1 && 
                      page < totalPages && 
                      Math.abs(page - currentPage) <= 1
                    )
                    .map((page) => (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        className="w-7 h-7 sm:w-8 sm:h-8 p-0 text-xs sm:text-sm"
                      >
                        {page}
                      </Button>
                    ))
                  }
                  
                  {/* Show ellipsis if current page is far from end */}
                  {currentPage < totalPages - 2 && (
                    <span className="px-1 text-gray-500 text-xs sm:text-sm">...</span>
                  )}
                  
                  {/* Always show last page */}
                  {totalPages > 1 && (
                    <Button
                      variant={currentPage === totalPages ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePageChange(totalPages)}
                      className="w-7 h-7 sm:w-8 sm:h-8 p-0 text-xs sm:text-sm"
                    >
                      {totalPages}
                    </Button>
                  )}
                </>
              )}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="text-xs sm:text-sm"
            >
              <span className="hidden sm:inline mr-1">Next</span>
              <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Information footer */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
        <div className="flex items-start gap-2">
          <Info className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-blue-900">Booking Information</h4>
            <p className="text-xs text-blue-800 mt-1">
              Select an ambulance type above to see pricing and availability. 
              Emergency bookings are prioritized automatically. All prices include base fare and per-kilometer charges.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AmbulanceTypes

